// A mission's six-level classification write (agion/Agion#2831, challenge
// record 2026-10-05-governance-a5-a6-a8-adoption-contracts, F-M1, F-H6). The
// explicit Save (PATCH /v1/missions/{id}) is the one writer that adopts a
// level or raises it; orchestration-service decides everything: which levels
// may be set, what blocks adoption, who loses access and whether the caller
// acknowledged it. This module builds the wire body and types the writer's
// answers; it decides nothing.
//
// Wire contract (orchestration-service 87506a7f18):
// - body {access_classification: level} and, once acknowledged,
//   classification_confirmation: the token of the impact the caller saw.
// - 409 {error: mission_classification_confirmation_required,
//   classification_impact: {from, to, affected_members, confirmation_token}}:
//   the acknowledgment is missing or stale; the answer is the current impact.
// - refusals {code, error, blockers?, source?, missing_facts?}:
//   409 adoption_blocked, 400 classification_cannot_be_cleared,
//   409 classification_cannot_be_lowered, 409 classification_sideways (a
//   move between Customer confidential and Internal, which are not
//   comparable; agion/Agion#2951), 503 governance_unavailable,
//   409 governance_inactive, 422 source_policy_incomplete, 422
//   source_unclassified (a bound source with no access classification,
//   named like source_policy_incomplete), and the adoption
//   trigger's 409 adoption_requires_next_only / adoption_policy_off /
//   adoption_requires_current_tenant, 400 invalid_access_classification.
import { isApiFailure } from '@agion/ui/api-failure'
import { api } from '@/api'
import { keysToCamel } from '@/shared/lib/utils'
import { blockersOf, type ReadinessBlocker } from './governance-readiness.service'

/** The stable refusal codes of the classification writer. */
export const CLASSIFICATION_WRITE_REFUSALS = [
  'adoption_blocked',
  'classification_cannot_be_cleared',
  'classification_cannot_be_lowered',
  'classification_sideways',
  'governance_unavailable',
  'governance_inactive',
  'source_policy_incomplete',
  'source_unclassified',
  'adoption_requires_next_only',
  'adoption_policy_off',
  'adoption_requires_current_tenant',
  'invalid_access_classification',
] as const
export type ClassificationWriteRefusalCode = (typeof CLASSIFICATION_WRITE_REFUSALS)[number]

/** One person who loses access, as the impact names them. The id is for links only. */
export interface ImpactMember {
  userId: string
  displayName: string
}

export interface ClassificationImpact {
  /** null for an adoption: the mission had the legacy audience. */
  from: string | null
  to: string
  affectedMembers: ImpactMember[]
  confirmationToken: string
}

export type ClassificationWriteFailure =
  | { kind: 'impact'; impact: ClassificationImpact }
  | { kind: 'refused'; code: string; blockers: ReadinessBlocker[]; missingFacts: string[]; sourceName?: string }
  | { kind: 'other' }

/** The body of the Save that adopts or raises the level: the level only, with the acknowledgment once given. */
export function classificationWriteBody(level: string, confirmation?: string): Record<string, unknown> {
  return { access_classification: level, ...(confirmation ? { classification_confirmation: confirmation } : {}) }
}

/**
 * The explicit Save of a classification body (classificationWriteBody):
 * PATCH /v1/missions/{id}, rejecting with the writer's answer. For a caller
 * without the mission detail's own save, such as the publish dialog
 * (agion/Agion#3203).
 */
export async function saveMissionClassification(missionId: string, body: Record<string, unknown>): Promise<void> {
  await api.patch<unknown>(`missions/${encodeURIComponent(missionId)}`, body)
}

type Raw = Record<string, unknown>

const record = (value: unknown): Raw | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : undefined

function impactOf(value: unknown): ClassificationImpact | undefined {
  const raw = record(value)
  if (!raw) return undefined
  const { from, to, affectedMembers, confirmationToken } = raw
  if (typeof to !== 'string' || to === '' || typeof confirmationToken !== 'string' || confirmationToken === '') return undefined
  if (from !== null && from !== undefined && typeof from !== 'string') return undefined
  if (!Array.isArray(affectedMembers)) return undefined
  const members = affectedMembers.flatMap((entry): ImpactMember[] => {
    const member = record(entry)
    if (!member || typeof member.userId !== 'string') return []
    return [{ userId: member.userId, displayName: typeof member.displayName === 'string' ? member.displayName : '' }]
  })
  return { from: typeof from === 'string' && from !== '' ? from : null, to, affectedMembers: members, confirmationToken }
}

/** What a failed classification Save means: an impact to acknowledge, a coded refusal, or anything else. */
export function classificationWriteFailureOf(error: unknown): ClassificationWriteFailure {
  if (!isApiFailure(error)) return { kind: 'other' }
  const body = record(keysToCamel(error.data))
  if (!body) return { kind: 'other' }
  if (error.status === 409 && body.error === 'mission_classification_confirmation_required') {
    const impact = impactOf(body.classificationImpact)
    return impact ? { kind: 'impact', impact } : { kind: 'other' }
  }
  const code = typeof body.code === 'string' ? body.code : undefined
  if (!code || !(CLASSIFICATION_WRITE_REFUSALS as readonly string[]).includes(code)) return { kind: 'other' }
  const source = record(body.source)
  const missingFacts = Array.isArray(body.missingFacts) ? body.missingFacts.filter((fact): fact is string => typeof fact === 'string') : []
  return {
    kind: 'refused',
    code,
    blockers: blockersOf(body.blockers),
    missingFacts,
    ...(typeof source?.name === 'string' && source.name !== '' ? { sourceName: source.name } : {}),
  }
}
